import { useNavigate, useParams } from 'react-router-dom'
import { CSVLink } from 'react-csv'
import { SweepstakesDetailsHeader } from './SweepstakeDetailsHeader'
import { useGetSweepstakeByIdWithParticipatingLocations } from 'hooks/api/useGetSweepstakeByIdWithParticipatingLocations'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { isNil, uniqBy } from 'lodash'
import { Box, Button, Stack, Typography } from '@mui/material'
import { SearchField } from 'components/SearchField'
import { useMemo, useState } from 'react'
import { useDebounce } from '@uidotdev/usehooks'
import { useGetContestLicensedEstablishmentSearch } from 'hooks/api/LicensedEstablishment/useGetContestLicensedEstablishmentSearch'
import { minimumSearchLength, formatDateTime } from 'utils/util'
import { useSweepstakeParticipatingLocationsAndEnrollmentAgreement } from 'stores/useSweepstakeParticipatingLocationsAndEnrollmentAgreement'
import { ParticipatingLocationList } from '../SweepstakesEnrollLocations/ParticipatingLocationList'
import { useGetParticipatingLocations } from 'hooks/api/Sweepstake/useGetParticipatingLocations'
import { isPresent } from '@jjvgaming/player-payback-library'
import { type LicensedEstablishment } from 'types/api'

export const SweepstakeParticipatingLocationsPage = () => {
  const { id: idParam } = useParams()
  const sweepstakeId = Number(idParam)
  const sweepstakeQuery = useGetSweepstakeByIdWithParticipatingLocations(
    Number(sweepstakeId)
  )
  const [searchKey, setSearchKey] = useState('')
  const debouncedSearchKey = useDebounce(searchKey, 500)
  const navigate = useNavigate()
  const grandPrizeOnly =
    sweepstakeQuery.data?.grandPrizeType === 'GrandPrizeOnly'

  const { setParticipatingLocations, setEnrollmentDetails } =
    useSweepstakeParticipatingLocationsAndEnrollmentAgreement()

  const licensedEstablishmentSearchQuery =
    useGetContestLicensedEstablishmentSearch(sweepstakeId, debouncedSearchKey)

  const participatingLocationsQuery = useGetParticipatingLocations({
    contestId: sweepstakeId,
  })

  const preferredQuery = useMemo(() => {
    if (debouncedSearchKey.length >= minimumSearchLength) {
      return licensedEstablishmentSearchQuery
    }
    return participatingLocationsQuery
  }, [
    debouncedSearchKey,
    licensedEstablishmentSearchQuery,
    participatingLocationsQuery,
  ])

  interface ParticipatingLocationsCsv {
    IGBNumber: string
    LicensedEstablishment: string
    OptInDate: string | null | undefined
    OptInUser: string | null | undefined
    Email: string | null | undefined
    Decal: boolean | undefined
    Banner: boolean | undefined
    Notes: string | null | undefined
    AccountManagerEmail: string | null | undefined
    AccountManagerJobTitle: string | null | undefined
    AccountManagerName: string | null | undefined
    AccountManagerPhoneNumber: string | null | undefined
    SalesRepEmail: string | null | undefined
    SalesRepJobTitle: string | null | undefined
    SalesRepName: string | null | undefined
    SalesRepPhoneNumber: string | null | undefined
  }

  // Get all related organizations to show in the table
  const allOrganizations = useMemo(() => {
    const allOrganizations = sweepstakeQuery.data?.licensedEstablishments?.map(
      (le) => le.organization
    )
    return uniqBy(allOrganizations, 'id').filter(isPresent)
  }, [sweepstakeQuery.data])

  // Get all related corporate account to show in the table
  const allCorporateAccounts = useMemo(() => {
    const allCorporateAccounts =
      sweepstakeQuery.data?.licensedEstablishments?.map(
        (le) => le.corporateAccount
      )
    return uniqBy(allCorporateAccounts, 'id').filter(isPresent)
  }, [sweepstakeQuery.data])

  const locations: LicensedEstablishment[] = useMemo(() => {
    if (!Array.isArray(preferredQuery.data)) {
      return []
    }

    return preferredQuery.data
      .map((sweepstakeLocation) => {
        const enrolledLE = sweepstakeLocation.licensedEstablishment
        if (enrolledLE == null || sweepstakeLocation.createdOn == null) {
          return null
        }
        return {
          ...enrolledLE,
          name: enrolledLE.standardName ?? '',
          licenseNumber: enrolledLE.licenseNumber,
          id: enrolledLE.id,
          addresses: enrolledLE.addresses,
          emailAddress: sweepstakeLocation.emailAddress,
          dateAdded: sweepstakeLocation.createdOn,
          signage1: sweepstakeLocation.signage1,
          signage2: sweepstakeLocation.signage2,
          accountManagerEmail:
            sweepstakeLocation.licensedEstablishment?.accountManagerEmail,
          accountManagerJobTitle:
            sweepstakeLocation.licensedEstablishment?.accountManagerJobTitle,
          accountManagerName:
            sweepstakeLocation.licensedEstablishment?.accountManagerName,
          accountManagerPhoneNumber:
            sweepstakeLocation.licensedEstablishment?.accountManagerPhoneNumber,
          salesRepEmail:
            sweepstakeLocation.licensedEstablishment?.salesRepEmail,
          salesRepJobTitle:
            sweepstakeLocation.licensedEstablishment?.salesRepJobTitle,
          salesRepName: sweepstakeLocation.licensedEstablishment?.salesRepName,
          salesRepPhoneNumber:
            sweepstakeLocation.licensedEstablishment?.salesRepPhoneNumber,
        }
      })
      .filter(isPresent)
  }, [preferredQuery])

  // Filter organizations when searching
  const filteredOrganizations = useMemo(() => {
    if (searchKey.length >= minimumSearchLength) {
      const organizationIds = new Set(
        locations.map((location) => location.organizationId)
      )
      return allOrganizations.filter((org) => organizationIds.has(org.id))
    }
    return allOrganizations
  }, [locations, allOrganizations, searchKey])

  // Filter corporate accounts when searching
  const filteredCorporateAccounts = useMemo(() => {
    if (searchKey.length >= minimumSearchLength) {
      const corporateAccountIds = new Set(
        locations.map((location) => location.corporateAccountId)
      )
      return allCorporateAccounts.filter((acc) =>
        corporateAccountIds.has(acc.id)
      )
    }
    return allCorporateAccounts
  }, [locations, allCorporateAccounts, searchKey])

  // Participating locations CSV data
  const locationsCsv: ParticipatingLocationsCsv[] = useMemo(() => {
    if (!Array.isArray(preferredQuery.data)) {
      return []
    }

    return preferredQuery.data
      .map((sweepstakeLocation) => {
        const enrolledLE = sweepstakeLocation.licensedEstablishment
        if (enrolledLE == null || sweepstakeLocation.createdOn == null) {
          return null
        }
        return {
          IGBNumber: enrolledLE.licenseNumber,
          LicensedEstablishment:
            enrolledLE.standardName?.replace(/["',]/g, '') ?? '',
          OptInDate: formatDateTime(sweepstakeLocation.createdOn),
          OptInUser: sweepstakeLocation.createdBy,
          Email: sweepstakeLocation.emailAddress,
          Decal: sweepstakeLocation.signage1,
          Banner: sweepstakeLocation.signage2,
          Notes: sweepstakeLocation.notes,
          AccountManagerEmail:
            sweepstakeLocation.licensedEstablishment?.accountManagerEmail,
          AccountManagerJobTitle:
            sweepstakeLocation.licensedEstablishment?.accountManagerJobTitle,
          AccountManagerName:
            sweepstakeLocation.licensedEstablishment?.accountManagerName,
          AccountManagerPhoneNumber:
            sweepstakeLocation.licensedEstablishment?.accountManagerPhoneNumber,
          SalesRepEmail:
            sweepstakeLocation.licensedEstablishment?.salesRepEmail,
          SalesRepJobTitle:
            sweepstakeLocation.licensedEstablishment?.salesRepJobTitle,
          SalesRepName: sweepstakeLocation.licensedEstablishment?.salesRepName,
          SalesRepPhoneNumber:
            sweepstakeLocation.licensedEstablishment?.salesRepPhoneNumber,
        }
      })
      .filter(isPresent)
  }, [preferredQuery])

  if (sweepstakeQuery.isError || isNil(sweepstakeQuery.data)) {
    return <Box>An error occurred fetching the sweepstake.</Box>
  }

  return (
    <SweepstakesDetailsHeader
      sweepstake={sweepstakeQuery.data}
      currentTab={`/Contests/Sweepstakes/${Number(sweepstakeQuery.data?.id)}/ParticipatingLocations`}
    >
      <Typography variant="h2">Participating Locations</Typography>
      {!grandPrizeOnly && (
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          justifyContent="space-between"
          marginTop={'20px'}
        >
          <SearchField
            placeholder="Search Participating Locations"
            onChange={(e) => setSearchKey(e.target.value)}
            fullWidth
          />

          <Button
            fullWidth
            sx={{
              display: 'flex',
              justifyContent: 'left',
            }}
            onClick={() => {
              setParticipatingLocations(undefined)
              setEnrollmentDetails(undefined)
              navigate(
                `/Contests/Sweepstakes/${Number(sweepstakeQuery?.data?.id)}/Manage-participating-locations`
              )
            }}
          >
            Enroll Locations
          </Button>
        </Stack>
      )}
      {preferredQuery.isPending ? (
        <ActivityIndicator marginTop={8} />
      ) : (
        <ParticipatingLocationList
          les={locations}
          organizations={filteredOrganizations}
          corporateAccounts={filteredCorporateAccounts}
          contestId={sweepstakeId}
        />
      )}
      {(preferredQuery.isError || isNil(preferredQuery.data)) ?? (
        <Box>An error occurred fetching the sweepstake.</Box>
      )}
      {!grandPrizeOnly && (
        <CSVLink
          data={locationsCsv}
          filename={'participating locations list.csv'}
        >
          <Button
            variant="contained"
            fullWidth
            sx={{
              marginTop: 5,
            }}
          >
            Export List
          </Button>
        </CSVLink>
      )}
    </SweepstakesDetailsHeader>
  )
}
